import { ExpandMore, AssignmentTurnedIn, ChatBubble, AttachFile } from '@mui/icons-material';
import {
  Button,
  useTheme,
  Box,
  Divider,
  useMediaQuery,
  Chip,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Theme,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useMemo, useState, useEffect } from 'react';
import { TodoDetailModal } from './punchlist-item';
import { Table, Link, TableLayout } from '../../../components';
import { IPunchlistTopLevel, IPunchlistItem } from '../../../models';
import { sortPunchlistTodoItems } from '../../../helpers';

interface IPunchlistAccordion {
  punchlistItem: IPunchlistTopLevel;
  isAllAccodionsExpanded?: boolean;
  refetchData: () => void;
  query?: string;
}

export const PunchlistAccordion: FC<IPunchlistAccordion> = ({
  punchlistItem,
  isAllAccodionsExpanded,
  refetchData,
  query
}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const theme = useTheme();
  const [isExpanded, setExpanded] = useState<boolean>(isAllAccodionsExpanded ?? false);
  const [isTodoModalOpen, showTodoModal] = useState<boolean>(false);
  const [currentTodoId, setCurrentTodoId] = useState<number | null>(null);

  useEffect(() => {
    setExpanded(isAllAccodionsExpanded);
  }, [isAllAccodionsExpanded]);

  const getActionTitle = (item: IPunchlistItem): string => {
    let action = 'Assigned To:';
    if (
      (item.itemType === 'Comment' || item.itemType === 'FileUpload') &&
      item.status !== 'Complete'
    ) {
      action = 'Posted By:';
    }

    if (item.status === 'Complete') {
      action = 'Completed By:';
    }
    return action;
  };

  const getTypeData = (
    item: IPunchlistItem
  ): { icon: JSX.Element; label: string; color: string } => {
    const iconStyles = {
      fontSize: '1rem',
      marginLeft: `10px !important`
    };
    let icon = <AssignmentTurnedIn sx={{ color: `#fff !important`, ...iconStyles }} />;
    let color = theme.palette.primary.dark;
    let label = 'To-Do';
    if (item.itemType === 'Comment') {
      icon = <ChatBubble sx={{ color: `#fff !important`, ...iconStyles }} />;
      color = theme.palette.primary.main;
      label = 'Comment';
    }

    if (item.itemType === 'FileUpload') {
      icon = <AttachFile sx={iconStyles} />;
      color = theme.palette.tertiary.main;
      label = 'File Upload';
    }
    return {
      icon,
      color,
      label
    };
  };
  const tableDataColumns = useMemo(() => {
    return [
      {
        Header: 'Type',
        accessor: 'itemType',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistItem } };
        }) => {
          const { icon, label, color } = getTypeData(original);
          return (
            <Chip
              icon={icon}
              sx={{
                backgroundColor: color,
                color: original.itemType === 'TodoItem' ? '#fff' : '#000'
              }}
              label={label}
            />
          );
        }
      },
      {
        Header: 'Description',
        accessor: 'punchListItemDescription',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistItem } };
        }) => {
          return (
            <Box
              component="span"
              sx={original.status === 'Complete' ? { textDecoration: 'line-through' } : undefined}
            >
              <Button
                sx={{ textAlign: 'left', textTransform: 'none' }}
                onClick={() => {
                  setCurrentTodoId(original.punchListItemId);
                  showTodoModal(true);
                }}
              >
                <div
                  style={
                    original.itemIsAttachedToFwtOrChecklist
                      ? { color: theme.palette.error.main }
                      : null
                  }
                  dangerouslySetInnerHTML={{ __html: original.punchListItemDescription }}
                />
              </Button>
            </Box>
          );
        }
      },
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription'
      },
      {
        Header: 'Action',
        accessor: 'action',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistItem } };
        }) => {
          const params = new URLSearchParams();
          params.append('assignedTo', original.assignedTo);
          params.append('vendorId', `${original.partnerId}`);
          return (
            <span>
              <span style={{ fontWeight: 'bold' }}>{getActionTitle(original)}</span>{' '}
              <Link to={`/production/punchlists/todos?${params.toString()}`}>
                {original.completedByDisplayName ||
                  original.assignedToDisplayName ||
                  original.assignedTo}
              </Link>
            </span>
          );
        }
      },
      {
        Header: 'Last Updated',
        accessor: 'lastUpdated'
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Accordion
        expanded={isExpanded}
        onChange={(_, expanded) => {
          setExpanded(expanded);
        }}
        className={classes.root}
      >
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              <Link to={`/production/punchlists/${punchlistItem.punchListId}?${query}`}>
                {punchlistItem.punchListName}
              </Link>
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          {isMobile ? (
            <>
              {punchlistItem.punchListItems.map((item, index) => {
                const { icon, label, color } = getTypeData(item);
                return (
                  <Box
                    key={`punchlist-item-accordion-${item.punchListItemId}-${item?.punchlistId}`}
                    marginBottom="1rem"
                  >
                    <TableLayout
                      label="Type:"
                      value={
                        <Chip
                          icon={icon}
                          sx={{
                            backgroundColor: color,
                            color: item.itemType === 'TodoItem' ? '#fff' : '#000'
                          }}
                          label={label}
                        />
                      }
                    />
                    <TableLayout
                      label="Title:"
                      value={
                        <Box
                          component="span"
                          sx={
                            item.status === 'Complete'
                              ? { textDecoration: 'line-through' }
                              : undefined
                          }
                        >
                          <Button
                            sx={{ textAlign: 'left', textTransform: 'none' }}
                            onClick={() => {
                              setCurrentTodoId(item.punchListItemId);
                              showTodoModal(true);
                            }}
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: item.punchListItemDescription
                              }}
                            />
                          </Button>
                        </Box>
                      }
                    />
                    <TableLayout label="Neighborhood:" value={item.neighborhoodDescription} />
                    <TableLayout label={getActionTitle(item)} value={item.assignedToDisplayName} />
                    <TableLayout label="Last Updated:" value={item.lastUpdated} />
                    <Divider />
                  </Box>
                );
              })}
            </>
          ) : (
            <>
              <Table
                columns={tableDataColumns}
                data={
                  sortPunchlistTodoItems(punchlistItem.punchListItems, 'status') as IPunchlistItem[]
                }
                isLoading={false}
                noResultsText="No Data"
                useTableProps={{
                  initialState: {
                    pageSize: 5
                  }
                }}
                labelRowsPerPage="Items per punchlist:"
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <TodoDetailModal
        open={isTodoModalOpen}
        punchlistItemId={currentTodoId}
        handleClose={(shouldRefresh?: boolean, shouldCloseModal = true) => {
          if (shouldCloseModal) {
            showTodoModal(false);
            setCurrentTodoId(null);
          }

          if (shouldRefresh) {
            refetchData();
          }
        }}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    '@media (min-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'center'
    },
    '&:first-child': {
      marginTop: 0
    }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    width: '100%'
  }
}));
